import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DomainTermInfo } from "@/components/ui/domain-term-info";
import { Label } from "@/components/ui/label";
import { type Pha } from "@/hooks/use-pha";
import { PhaCombobox } from "./PhaCombobox";
import { DateField, ResultBanner, SectionDivider, SelectField, StepHead, TextField, YesNoToggle } from "./fields";
import { BRACKET_LABEL, DEFAULT_FAMILY_SIZE, tenantEmailError } from "./data";
import type { Eligibility, EligibilityResult, Section8PropertyOption, WizardForm } from "./data";

export type Setter = <K extends keyof WizardForm>(key: K, value: WizardForm[K]) => void;

interface StepProps {
  form: WizardForm;
  set: Setter;
}

const money = (n: number) => `$${n.toLocaleString()}`;

function passDescription(result: EligibilityResult): string {
  const area = result.hudAreaName ? ` in ${result.hudAreaName}` : "";
  const cap = result.limits ? `, at or below the ${money(result.limits.low)} Low Income cap` : "";
  return `Annual income ${money(result.annualIncome)}${area}${cap} (family of ${DEFAULT_FAMILY_SIZE}). Steps 2–5 are now unlocked.`;
}

function failDescription(result: EligibilityResult): string {
  switch (result.error) {
    case "over_limit":
      return `Annual income ${money(result.annualIncome)} exceeds the Low Income limit${
        result.limits ? ` of ${money(result.limits.low)}` : ""
      }${result.hudAreaName ? ` for ${result.hudAreaName}` : ""} (family of ${DEFAULT_FAMILY_SIZE}).`;
    case "no_zip_match":
      return "No HUD income area found for this property's ZIP code.";
    case "no_income_data":
      return `No HUD income-limit data for ${result.hudAreaName ?? "this area"}.`;
    default:
      return "Select a property and enter the tenant's monthly salary, then re-run the check.";
  }
}

export function EligibilityStep({ form, set, properties, eligibility, result, checking, onCheck }: StepProps & {
  properties: Section8PropertyOption[];
  eligibility: Eligibility;
  result: EligibilityResult | null;
  checking: boolean;
  onCheck: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <StepHead title="Step 1 — Tenant eligibility"
        desc={`Checks the tenant's income against HUD limits for the property's area (family of ${DEFAULT_FAMILY_SIZE}). Later steps unlock once this passes.`} />
      <TextField id="tenantName" label="Tenant name" value={form.tenantName}
        onChange={(v) => set("tenantName", v)} placeholder="e.g. Marcus Reyes" />
      <div className="grid grid-cols-2 gap-4">
        <SelectField id="property" label="Property" required value={form.property}
          onChange={(v) => set("property", v)} options={properties}
          placeholder={properties.length ? "Select property" : "No voucher-accepting listings"} />
        <TextField id="salary" label="Tenant monthly salary" value={form.salary}
          onChange={(v) => set("salary", v)} prefix="$" placeholder="3,200" type="number"
          helper="Annualized (× 12) and compared to HUD limits." />
      </div>
      <div className="pt-1">
        <Button type="button" variant={eligibility === "idle" ? "default" : "outline"}
          onClick={onCheck} disabled={checking || !form.property || !form.salary}>
          {checking
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={2} />
            : <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={2} />}
          {checking ? "Checking…" : eligibility === "idle" ? "Check eligibility" : "Re-run check"}
        </Button>
      </div>
      {eligibility === "pass" && result && (
        <ResultBanner variant="pass"
          title={`Tenant is eligible — ${result.bracket ? BRACKET_LABEL[result.bracket] : "qualifies"}`}
          desc={passDescription(result)} />
      )}
      {eligibility === "fail" && result && (
        <ResultBanner variant="fail" title="Tenant not eligible" desc={failDescription(result)} />
      )}
    </div>
  );
}

export function VoucherStep({ form, set }: StepProps) {
  const emailError = tenantEmailError(form.tenantEmail);
  return (
    <div className="flex flex-col gap-4">
      <StepHead title="Step 2 — Tenant contact & voucher"
        desc="Capture how to reach the tenant and the monthly voucher amount." />
      <div className="grid grid-cols-2 gap-4">
        <TextField id="tenantPhone" label="Tenant phone" value={form.tenantPhone}
          onChange={(v) => set("tenantPhone", v)} placeholder="(555) 010-2233" />
        <TextField id="tenantEmail" label="Tenant email" type="email" value={form.tenantEmail}
          onChange={(v) => set("tenantEmail", v)} placeholder="tenant@email.com"
          error={emailError ?? undefined} />
      </div>
      <SectionDivider>Voucher details</SectionDivider>
      <TextField id="voucherAmount" label="Voucher amount" type="number" prefix="$" value={form.voucherAmount}
        onChange={(v) => set("voucherAmount", v)} placeholder="1,200" helper="Monthly Housing Assistance Payment the voucher covers." />
    </div>
  );
}

export function PHAStep({ form, set, phaState, phaLat, phaLng }: StepProps & {
  phaState?: string;
  phaLat?: number;
  phaLng?: number;
}) {
  const selectPha = (pha: Pha) => {
    set("phaName", pha.pha_name);
    set("phaParticipantCode", pha.participant_code);
    set("phaPhone", pha.pha_phone ?? pha.exec_director_phone ?? "");
    set("phaEmail", pha.pha_email ?? pha.exec_director_email ?? "");
  };
  const manualPha = (name: string) => {
    set("phaName", name);
    set("phaParticipantCode", "");
  };
  const clearPha = () => {
    set("phaName", "");
    set("phaParticipantCode", "");
    set("phaPhone", "");
    set("phaEmail", "");
  };
  return (
    <div className="flex flex-col gap-4">
      <StepHead title="Step 3 — Public Housing Authority"
        desc="Search the HUD directory — picking a PHA fills its phone and email. Not listed? Type a custom name." />
      <div>
        <Label htmlFor="phaName" className="mb-1.5 inline-flex items-center gap-1">
          <span>PHA name</span>
          <span className="text-destructive">*</span>
          <DomainTermInfo termId="PHA" />
        </Label>
        <PhaCombobox value={form.phaName} state={phaState} lat={phaLat} lng={phaLng}
          onSelect={selectPha} onManual={manualPha} onClear={clearPha} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TextField id="phaPhone" label="PHA phone" value={form.phaPhone}
          onChange={(v) => set("phaPhone", v)} placeholder="(555) 700-1100" />
        <TextField id="phaEmail" label="PHA email" type="email" value={form.phaEmail}
          onChange={(v) => set("phaEmail", v)} placeholder="intake@pha.gov" />
      </div>
      <TextField id="askingRent" label="Asking rent" type="number" prefix="$" value={form.askingRent}
        onChange={(v) => set("askingRent", v)} placeholder="1,251" />
    </div>
  );
}

export function ApprovalStep({ form, set }: StepProps) {
  return (
    <div className="flex flex-col gap-4">
      <StepHead title="Step 4 — Submission & approval"
        desc="Track the PHA submission and capture the approval date once granted." />
      <div className="grid grid-cols-2 gap-4">
        <TextField id="paymentStandard" label="Payment standard" type="number" prefix="$" term="payment_standard"
          value={form.paymentStandard} onChange={(v) => set("paymentStandard", v)} placeholder="1,400" />
        <TextField id="utilityAllowance" label="Utility allowance" type="number" prefix="$" term="utility_allowance"
          value={form.utilityAllowance} onChange={(v) => set("utilityAllowance", v)} placeholder="85" />
      </div>
      <DateField id="submissionDate" label="Submission date" value={form.submissionDate}
        onChange={(v) => set("submissionDate", v)} />
      <div>
        <p className="mb-2 text-[13px] font-medium text-foreground">Approved by PHA?</p>
        <YesNoToggle value={form.approved} onChange={(v) => {
          set("approved", v);
          if (v !== "yes") set("approvalDate", "");
        }} />
      </div>
      {form.approved === "yes" && (
        <DateField id="approvalDate" label="Approval date" required value={form.approvalDate}
          onChange={(v) => set("approvalDate", v)} />
      )}
    </div>
  );
}

export function MoveInStep({ form, set }: StepProps) {
  return (
    <div className="flex flex-col gap-4">
      <StepHead title="Step 5 — Move-in & HAP"
        desc="Record the move-in and the start of Housing Assistance Payments." />
      <DateField id="moveInDate" label="Move-in date" value={form.moveInDate}
        onChange={(v) => set("moveInDate", v)} />
      <div>
        <p className="mb-2 text-[13px] font-medium text-foreground">Has HAP started?</p>
        <YesNoToggle value={form.hapStarted ? "yes" : ""} yesLabel="HAP started" noLabel="Not yet"
          onChange={(v) => {
            const started = v === "yes";
            set("hapStarted", started);
            if (!started) set("hapStartDate", "");
          }} />
      </div>
      {form.hapStarted && (
        <DateField id="hapStartDate" label="HAP start date" required value={form.hapStartDate}
          onChange={(v) => set("hapStartDate", v)} term="HAP" />
      )}
    </div>
  );
}
